import React, { useRef, useState } from "react";
import EventCard from "./EventCard";
import EventButton, {
  EventButtonVariant,
} from "../../shared/components/EventButton";
import ProgressCircle from "../../shared/components/ProgressCircle";
import { components } from "../../../../../api/spec";

interface GuestListCardProps {
  guestStats: components["schemas"]["EventAttendeesSummaryResponse"] | null;
  hasGeneratedSetups: boolean;
  onSendInviteLink: () => void;
  onViewFullGuestList?: () => void;
  onViewGuestAdd?: () => void;
  eventId: string | undefined;
}

const GuestListCard: React.FC<GuestListCardProps> = ({
  guestStats,
  hasGeneratedSetups,
  onSendInviteLink,
  onViewFullGuestList,
  onViewGuestAdd,
  eventId,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadResult, setUploadResult] = useState<null | {
    created: number;
    errors: { row: number; reason: string }[];
  }>(null);

  const handleDownloadExampleCSV = async () => {
    try {
      const response = await fetch(
        "/api/v1/sitch-events/example-csv-attendees/",
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `attendees_example_${new Date().toLocaleString()}.csv`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
    } catch (error) {
      console.error("Error downloading CSV example:", error);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      if (!eventId) {
        console.error("eventId is required for uploading attendees");
        return;
      }

      const res = await fetch(
        `/api/v1/sitch-events/upload-event-attendees/?event=${eventId}`,
        {
          method: "POST",
          body: formData,
        },
      );

      const data = await res.json();
      setUploadResult(data);
    } catch (error) {
      console.error("Failed to upload attendees:", error);
    }
  };

  if (!guestStats) return null;

  return (
    <EventCard title="GUEST LIST">
      <div className="text-center mb-4">
        <p className="text-white/90 text-md">
          {guestStats.num_women} women, {guestStats.num_men} men, and{" "}
          {guestStats.num_other} others.
        </p>
      </div>

      <ProgressCircle
        percentage={Math.round(
          (guestStats.num_profiles_completed / guestStats.num_attendees_total) *
            100,
        )}
        size={24}
        label="Sitch profiles completed"
        className="mb-4"
      />

      {onViewFullGuestList && (
        <EventButton
          onClick={onViewFullGuestList}
          variant={EventButtonVariant.PRIMARY_BLACK}
          className="mt-4 w-full mb-4"
          icon="📝"
        >
          View full guest list
        </EventButton>
      )}

      {onViewGuestAdd && (
        <EventButton
          onClick={onViewGuestAdd}
          variant={EventButtonVariant.PRIMARY_BLACK}
          className="mt-4 w-full mb-4"
          icon="➕"
        >
          Add guest via phone number
        </EventButton>
      )}

      <EventButton
        onClick={handleDownloadExampleCSV}
        variant={EventButtonVariant.PRIMARY_BLACK}
        className="mt-4 w-full mb-4"
        icon="📥"
      >
        Download CSV Example
      </EventButton>

      <EventButton
        onClick={handleUploadClick}
        variant={EventButtonVariant.PRIMARY_BLACK}
        className="mb-4 w-full"
        icon="📤"
      >
        Upload Attendees from CSV
      </EventButton>

      <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        className="hidden"
        onChange={handleFileChange}
      />

      {uploadResult && (
        <div className="bg-white/10 rounded-xl p-4 text-white text-sm mt-2 relative">
          <button
            type="button"
            onClick={() => setUploadResult(null)}
            className="absolute top-2 right-2 text-white hover:text-red-400"
            aria-label="Close"
          >
            ✖
          </button>
          <p className="mb-2">
            ✅ Successfully created: {uploadResult.created} attendee(s)
          </p>
          {uploadResult.errors.length > 0 && (
            <>
              <p className="mb-1">⚠️ Errors:</p>
              <ul className="list-disc pl-5 space-y-1">
                {uploadResult.errors.map((err) => (
                  <li key={err.row}>
                    Row {err.row}: {err.reason}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      )}

      {!hasGeneratedSetups && (
        <EventButton
          onClick={onSendInviteLink}
          variant={EventButtonVariant.TRANSPARENT}
          className="w-full"
          icon="🔗"
        >
          Send invite link
        </EventButton>
      )}
    </EventCard>
  );
};

GuestListCard.defaultProps = {
  onViewFullGuestList: undefined,
  onViewGuestAdd: undefined,
};

export default GuestListCard;
